/**
 * useUserTimezone Hook
 * Provides user's timezone and timezone utility functions
 */

import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  parseInTimezone,
  formatTime,
  formatDate,
  formatDateTime,
  formatRelative,
  getHours,
  getMinutes,
  toUTC,
  toISOWithTimezone,
  getCurrentTime,
  DEFAULT_TIMEZONE,
} from '@/utils/timezone';

export interface UseUserTimezone {
  /**
   * Current user's display timezone (user.timezone || tenant.default_timezone || 'UTC')
   */
  timezone: string;

  /**
   * Parse ISO datetime string in user's timezone
   */
  parse: (isoString: string) => ReturnType<typeof parseInTimezone>;

  /**
   * Format time in user's timezone (default: 'HH:mm')
   */
  formatTime: (isoString: string, format?: string) => string;

  /**
   * Format date in user's timezone (default: 'YYYY-MM-DD')
   */
  formatDate: (isoString: string, format?: string) => string;

  /**
   * Format datetime in user's timezone (default: 'YYYY-MM-DD HH:mm')
   */
  formatDateTime: (isoString: string, format?: string) => string;

  /**
   * Format relative time (e.g., "2 hours ago")
   */
  formatRelative: (isoString: string) => string;

  /**
   * Get hours in user's timezone (0-23)
   */
  getHours: (isoString: string) => number;

  /**
   * Get minutes in user's timezone (0-59)
   */
  getMinutes: (isoString: string) => number;

  /**
   * Convert local Date to UTC ISO string for API submission
   */
  toUTC: (localDate: Date) => string;

  /**
   * Convert dayjs object to ISO string WITH timezone offset
   * Use this for API submissions where backend needs timezone info
   */
  toISOWithTimezone: (dayjsObj: ReturnType<typeof parseInTimezone>) => string;

  /**
   * Get current time in user's timezone
   */
  getCurrentTime: () => ReturnType<typeof getCurrentTime>;
}

/**
 * Hook to access user's timezone and timezone utility functions
 *
 * @example
 * ```tsx
 * const { timezone, formatDateTime, toUTC } = useUserTimezone();
 *
 * // Display UTC timestamp in user's timezone
 * const displayTime = formatDateTime("2024-01-15T10:30:00Z");
 * // Output: "15.01.2024 11:30" (if user is in Europe/Belgrade)
 *
 * // Convert local date to UTC for API
 * const utcTime = toUTC(new Date());
 * // Output: "2024-01-15T10:30:00.000Z"
 * ```
 */
export const useUserTimezone = (): UseUserTimezone => {
  const { user } = useAuth();

  const timezone = useMemo(() => {
    if (!user) return DEFAULT_TIMEZONE;
    return user.timezone || user.tenant?.default_timezone || DEFAULT_TIMEZONE;
  }, [user]);

  return useMemo(
    () => ({
      timezone,
      parse: (isoString: string) => parseInTimezone(isoString, timezone),
      formatTime: (isoString: string, format?: string) =>
        formatTime(isoString, format, timezone),
      formatDate: (isoString: string, format?: string) =>
        formatDate(isoString, format, timezone),
      formatDateTime: (isoString: string, format?: string) =>
        formatDateTime(isoString, format, timezone),
      formatRelative: (isoString: string) =>
        formatRelative(isoString, timezone),
      getHours: (isoString: string) => getHours(isoString, timezone),
      getMinutes: (isoString: string) => getMinutes(isoString, timezone),
      toUTC: (localDate: Date) => toUTC(localDate, timezone),
      toISOWithTimezone: (dayjsObj) => toISOWithTimezone(dayjsObj),
      getCurrentTime: () => getCurrentTime(timezone),
    }),
    [timezone]
  );
};
